// src/pages/QuizPage/hooks/useMistakesQuiz.ts
import { useState, useEffect, useCallback, useRef } from "react";
import { userAnswersAPI } from "../../../api";
import { mapRawQuestions } from "../utils/quizLang";
import { shuffleArray, getShuffleSettings } from "../utils/shuffleUtils";
import type { Question, AnswerResult } from "../utils/quizTypes";

const AUTO_ADVANCE_DELAY = 600;
const AUTO_FINISH_DELAY = 1000;

type UiLang = "oz" | "uz" | "ru";

interface UseMistakesQuizOptions {
  lang: UiLang;
}

export function useMistakesQuiz({ lang }: UseMistakesQuizOptions) {
  const [rawQs, setRawQs] = useState<any[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [current, setCurrent] = useState(0);
  const [answered, setAnswered] = useState<Record<number, number>>({});
  const [answerMeta, setAnswerMeta] = useState<Record<number, AnswerResult>>(
    {},
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);

  const finishCalledRef = useRef(false);
  const answeredQuestionsRef = useRef<Set<number>>(new Set());

  // ─── INIT ──────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const res = await userAnswersAPI.getMistakes(lang);
        if (cancelled) return;
        const raw: any[] = res.data || [];

        if (raw.length === 0) {
          setIsEmpty(true);
          setFinished(true);
          setLoading(false);
          return;
        }

        const { shuffleQuestions } = getShuffleSettings();
        setRawQs(shuffleQuestions ? shuffleArray(raw) : raw);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.response?.data?.message || "Xatolar yuklanmadi");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
    // Lang o'zgarganda rawQs o'zgarmaydi (faqat mapping qayta bo'ladi)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── rawQs → questions mapping ─────────────────────────────────────
  useEffect(() => {
    if (!rawQs.length) return;
    setQuestions((prev) => mapRawQuestions(rawQs, lang, answerMeta, prev));
  }, [rawQs, lang, answerMeta]);

  // ─── Hisoblar ──────────────────────────────────────────────────────
  const correctCount = Object.keys(answered).filter(
    (k) => questions[Number(k)]?.correct_answer_index === answered[Number(k)],
  ).length;

  const wrongCount = Object.keys(answered).filter(
    (k) => questions[Number(k)]?.correct_answer_index !== answered[Number(k)],
  ).length;

  // ─── FINISH ─────────────────────────────────────────────────────────
  const finish = useCallback(() => {
    if (finishCalledRef.current) return;
    finishCalledRef.current = true;
    setFinished(true);
  }, []);

  // ─── ANSWER ─────────────────────────────────────────────────────────
  const handleAnswer = useCallback(
    async (orderIndex: number) => {
      if (answeredQuestionsRef.current.has(current) || finished) return;
      const q = questions[current];
      if (!q) return;
      answeredQuestionsRef.current.add(current);

      const isCorrect = q.correct_answer_index === orderIndex;

      // answered + answerMeta
      const newAnswered = { ...answered, [current]: orderIndex };
      setAnswered(newAnswered);
      setAnswerMeta((prev) => ({
        ...prev,
        [q.id]: {
          selectedIndex: orderIndex,
          isCorrect,
          correctAnswerIndex: q.correct_answer_index,
        },
      }));

      // To'g'ri javob → backend'da "tuzatildi" deb belgilash
      if (isCorrect) {
        try {
          await userAnswersAPI.markAsCorrect(q.id);
        } catch {
          // Silent fail — frontend ishlayveradi
        }

        // Auto-advance — oxirgi savol bo'lmasa
        if (current < questions.length - 1) {
          setTimeout(() => {
            setCurrent((p) => Math.min(questions.length - 1, p + 1));
          }, AUTO_ADVANCE_DELAY);
        }
      }

      // All answered → auto-finish
      if (Object.keys(newAnswered).length === questions.length) {
        setTimeout(() => finish(), AUTO_FINISH_DELAY);
      }
    },
    [answered, current, finished, questions, finish],
  );

  // ─── Reset ─────────────────────────────────────────────────────────
  const reset = useCallback(() => {
    finishCalledRef.current = false;
    answeredQuestionsRef.current = new Set();
    setRawQs([]);
    setQuestions([]);
    setAnswered({});
    setAnswerMeta({});
    setCurrent(0);
    setFinished(false);
    setIsEmpty(false);
    setError(null);
    setLoading(true);

    userAnswersAPI
      .getMistakes(lang)
      .then((res) => {
        const raw: any[] = res.data || [];
        if (raw.length === 0) {
          setIsEmpty(true);
          setFinished(true);
        } else {
          const { shuffleQuestions } = getShuffleSettings();
          setRawQs(shuffleQuestions ? shuffleArray(raw) : raw);
        }
      })
      .catch((e: any) => {
        setError(e?.response?.data?.message || "Xatolar yuklanmadi");
      })
      .finally(() => setLoading(false));
  }, [lang]);

  return {
    // State
    questions,
    current,
    answered,
    answerMeta,
    loading,
    error,
    finished,
    isEmpty,
    correctCount,
    wrongCount,
    // Actions
    handleAnswer,
    setCurrent,
    finish,
    reset,
  };
}
